import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismLoadingBar,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import SettingsCentralMappingCategoriesCategoriesList from 'lumen/components/settings/central-mapping/categories/categories-list';
import SettingsCentralMappingDropdownsDropdownMappingMoreActions from 'lumen/components/settings/central-mapping/dropdowns/dropdown-mapping-more-actions';
import SettingsCentralMappingModalsModalConfirmationDeleteCategory from 'lumen/components/settings/central-mapping/modals/modal-confirmation-delete-category';
import SettingsCentralMappingModalsModalHierarchyOnboarding from 'lumen/components/settings/central-mapping/modals/modal-hierarchy-onboarding';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type HotelSettingsCentralMappingHierarchyController from 'lumen/controllers/hotel/settings/central-mapping/hierarchy';

interface HotelSettingsCentralMappingHierarchySignature {
  Args: {
    controller: HotelSettingsCentralMappingHierarchyController;
    model: unknown;
  };
}

<template>
  {{#if @controller.model.hierarchy.isRunning}}
    <PrismLoadingBar />
  {{/if}}

  <SettingsPageTemplate
    @badge={{if
      @controller.showHierarchyPageBadge
      @controller.hierarchyPageBadgeText
    }}
    @isFullHeight={{true}}
    @title={{@controller.title}}
    class="central-mapping"
    data-test-id="central-mapping-hierarchy-page-template"
  >
    <:breadcrumb>
      <PrismBreadcrumb as |B|>
        <B.item
          @label={{t "settings.pms_mapping.mapping"}}
          @route="hotel.settings.central-mapping.menu"
        />
        <B.item @isCurrent={{true}} @label={{@controller.title}} />
      </PrismBreadcrumb>
    </:breadcrumb>

    <:headerAfter>
      <div class="flex flex-row gap-200">
        {{#unless @controller.isLoading}}
          {{#if @controller.isReadOnly}}
            {{#unless @controller.isHierarchyManagedByMarket}}
              <PrismButton
                @buttonType="ghost"
                @iconBefore="book"
                @label={{t "general.help_center"}}
                @onClick={{@controller.openHelpArticle}}
                data-test-id="central-mapping-hierarchy-help-center-link"
              />
            {{/unless}}

            {{#if @controller.isHierarchyManagedByMarket}}
              <PrismButton
                @buttonType="primary"
                @iconBefore={{if @controller.hasMarketEditAccess "edit-pencil"}}
                @label={{@controller.navigateToMarketButtonLabel}}
                data-test-id="central-mapping-mapping-navigate-to-market-button"
                {{on "click" @controller.onClickNavigateToMarket}}
              />
            {{/if}}

            <PrismButton
              @buttonType="secondary"
              @label={{@controller.navigateToMappingButtonLabel}}
              data-test-id="central-mapping-hierarchy-map-values-button"
              {{on "click" @controller.onClickMapValues}}
            />
          {{else}}
            <SettingsCentralMappingDropdownsDropdownMappingMoreActions
              @dropdownOptions={{@controller.moreActionsOptions}}
              @onSelect={{@controller.onSelectMoreActionsOption}}
            />

            {{#if @controller.isHierarchyManagedByMarket}}
              <PrismButton
                @buttonType="secondary"
                @iconBefore={{if @controller.hasMarketEditAccess "edit-pencil"}}
                @label={{@controller.navigateToMarketButtonLabel}}
                data-test-id="central-mapping-mapping-navigate-to-market-button"
                {{on "click" @controller.onClickNavigateToMarket}}
              />
            {{/if}}

            <PrismButton
              @buttonType="secondary"
              @isDisabled={{or
                @controller.updateHierarchyTask.isRunning
                (not @controller.isSaveButtonEnabled)
              }}
              @label={{t "settings.central_mapping.discard"}}
              @onClick={{@controller.onClickDiscardChanges}}
              data-test-id="central-mapping-mapping-discard-changes-button"
            />

            <PrismButton
              @isDisabled={{or
                @controller.updateHierarchyTask.isRunning
                (not @controller.isSaveButtonEnabled)
              }}
              @isLoading={{@controller.updateHierarchyTask.isRunning}}
              @label={{t "central_mapping.save_changes"}}
              data-test-id="central-mapping-hierarchy-save-changes-button"
              {{on "click" @controller.onClickSave}}
            />
          {{/if}}
        {{/unless}}
      </div>
    </:headerAfter>

    <:default>
      <div class="grid h-full auto-cols-fr grid-flow-col">
        <SettingsCentralMappingCategoriesCategoriesList
          @breakdown={{@controller.breakdown}}
          @categories={{@controller.rootCategories}}
          @deleteCategory={{@controller.deleteCategoryAndChildren}}
          @deleteCategoryWithConfirmationModal={{@controller.deleteCategoryWithConfirmationModal}}
          @depth={{0}}
          @isLoading={{@controller.isLoading}}
          @isReadOnly={{@controller.isReadOnly}}
          @maxDepth={{@controller.MAX_DEPTH}}
          @selectCategory={{@controller.selectCategory}}
          @selectedCategories={{@controller.selectedCategories}}
          @trackCategoryAdded={{@controller.trackCategoryAdded}}
          @trackCategoryRenamed={{@controller.trackCategoryRenamed}}
        />
      </div>
    </:default>
  </SettingsPageTemplate>

  {{#if @controller.showConfirmationModalDeleteCategory}}
    {{! and template helper doesn't narrow TS type, so use 2 separate if's }}
    {{#if @controller.categoryToDelete}}
      <SettingsCentralMappingModalsModalConfirmationDeleteCategory
        @breakdown={{@controller.breakdown}}
        @category={{@controller.categoryToDelete}}
        @onClose={{@controller.closeConfirmationModalDeleteCategory}}
        @onDiscard={{@controller.deleteCategory}}
      />
    {{/if}}
  {{/if}}

  {{#if @controller.showUserOnboardingModal}}
    <SettingsCentralMappingModalsModalHierarchyOnboarding
      @onClose={{@controller.closeUserOnboardingModal}}
    />
  {{/if}}
</template> satisfies TOC<HotelSettingsCentralMappingHierarchySignature>;
